<template>
  <div class="form">
    <form @submit.prevent>
      <CurrencyInput
        key="sellCoin"
        name="coin"
        type="text"
        label="Sell"
        placeholder="Coin amount"
        :defaultValue="{
          coinType: sellForm.coinType,
        }"
        :items="coinInputs"
        :isLoading="isFetchingData.coinType"
        :errors="sellOrderFormStore.getErrorByField('coinAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />
      <CurrencyInput
        key="sellFiat"
        name="fiat"
        type="text"
        label="Receive"
        placeholder="Fiat amount"
        :defaultValue="{
          fiatType: sellForm.fiatType,
        }"
        :items="fiatInputs"
        :isLoading="isFetchingData.fiatType"
        :errors="sellOrderFormStore.getErrorByField('fiatAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        type="text"
        name="blockchain"
        label="Selected blockchain"
        :key="'blockchain'"
        :disabled="disabledBlockchainSelect"
        :items="blockchainsByCoin"
        :defaultItem="defaultBlockchain"
        :isLoading="isFetchingData.blockchain"
        @dropdownValueUpdate="updateDropdown"
      />

      <PaymentInput
        key="sellPayments"
        :items="orderStore.filterPaymentsByFiatAndCoin"
        @paymentSelected="updateDropdown"
      />

      <ButtonBase
        :isDisabled="
          sellOrderFormStore.formHasErrors ||
          globalStore.getIsFormLoading ||
          sellOrderFormStore.feeData.isLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Create Order
      </ButtonBase>
      <p class="term-use" v-if="initialData?.termOfUse?.show">{{ initialData?.termOfUse?.message }}</p>
    </form>
  </div>
</template>

<script setup>
import { provide, ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { whenever } from "@vueuse/core";

import { useGlobalStore } from "@/stores/globalStore";
import { useOrderFormStore, settings } from "@/stores/forms/orderFormStore";
import { useSellOrderFormStore } from "@/stores/forms/sellOrderFormStore";

import useApiHelpers from "@/composables/api/helpers";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useTransactionalLimits from "@/composables/watchers/useTransactionalLimits";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";
import useOrderCreation from "@/composables/api/useOrderCreation";
import useOrderForm from "@/composables/shared/useOrderForm";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import CurrencyInput from "@/components/ui/inputs/CurrencyInput.vue";
import PaymentInput from "@/components/ui/inputs/PaymentInput.vue";
import FormSelect from "@/components/ui/selects/FormSelect.vue";

const router = useRouter();
const sellOrderFormStore = useSellOrderFormStore();
const orderStore = useOrderFormStore();
const globalStore = useGlobalStore();
const { postRequest } = useApiHelpers();
const { watchFormAndUpdateStore } = useWatchAndUpdate();
const { dynamicPricingCalculator } = useDynamicPricingCalculator();
const { setFormRefsAndUpdateStore } = useOrderCreation();
const { watchAndHandleFiatValidation, watchAndHandleCoinValidation } =
  useTransactionalLimits();
const { getFiats, getCoins, initOrder } = useOrderEndpoints();
const { findBlockchainsByCoin } = useOrderForm();
const identityQueueStore = useIdentityQueueStore();

const props = defineProps({
  initialData: {
    type: Object,
    default: () => {},
  },
});
const fiatInputs = ref([]);
const coinInputs = ref([]);
const provideUiDisable = ref({});
const blockchainsByCoin = ref([]);
const defaultBlockchain = ref("");
const sellForm = ref({
  fiatType: "",
});
const isFetchingData = ref({
  fiatAmount: true,
  coinAmount: true,
});

const disabledBlockchainSelect = computed(() => {
  if (
    blockchainsByCoin.value &&
    blockchainsByCoin.value.includes("No valid blockchains available")
  ) {
    return true;
  }

  return false;
});

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  if (value) sellForm.value[field] = value;
  dynamicPricingCalculator(field, value, sellForm, sellOrderFormStore);
};

const updateCurrencyInput = (inputData) => {
  const field = Object.keys(inputData)[0];
  const value = Object.values(inputData)[0];
  const lockFiat = field === "fiatAmount";

  sellForm.value[field] = value;
  sellForm.value.lockFiat = lockFiat;

  dynamicPricingCalculator(field, value, sellForm, sellOrderFormStore);
};

const changeLoadingValueByName = (name, isLoading) => {
  isFetchingData.value[name] = isLoading;
};

const setDefaultsByLocalStorage = () => {
  Object.keys(sellOrderFormStore.form).forEach((key) => {
    if (key === "fiatType") {
      const defaultFiatType =
        fiatInputs.value.indexOf(settings.defaultFiatType) >= 0
          ? settings.defaultFiatType
          : fiatInputs.value[0];
      sellForm.value.fiatType = defaultFiatType;
    } else if (key === "coinType") {
      const defaultCoinType =
        fiatInputs.value.indexOf(settings.defaultCoinType) >= 0
          ? settings.defaultCoinType
          : coinInputs.value[0];
      sellForm.value.coinType = defaultCoinType;
    } else {
      sellForm.value[key] = sellOrderFormStore.form[key].value;
    }
  });

  const lockFiat = sellOrderFormStore.form.lockFiat.value;
  const fieldToTriggerCalcBy = lockFiat ? "fiatAmount" : "coinAmount";
  dynamicPricingCalculator(
    fieldToTriggerCalcBy,
    sellForm.value[fieldToTriggerCalcBy],
    sellForm,
    sellOrderFormStore
  );
};

const runOnValidToken = async () => {
  const fiatResponse = await getFiats("sell");
  const coinResponse = await getCoins("sell");

  fiatInputs.value = fiatResponse.data.data.fiats.map((el) => {
    return el.fiat_code;
  });
  coinInputs.value = coinResponse.data.data.coins.map((el) => el.coin_code);

  sellOrderFormStore.setCoinData(coinResponse.data.data.coins);

  changeLoadingValueByName("fiatType", false);
  changeLoadingValueByName("coinType", false);
};

provide("isDisabledByKey", provideUiDisable);

watch(
  () => sellForm.value.coinType,
  () => {
    sellOrderFormStore.setCoinType(sellForm.value.coinType);
    findBlockchainsByCoin(
      sellOrderFormStore,
      sellForm,
      defaultBlockchain,
      blockchainsByCoin
    );
  },
  { deep: true }
);

watch(
  () => globalStore.$state.token,
  (val) => {
    if (val) {
      runOnValidToken();
    }
  },
  { deep: true, immediate: true }
);

// when coin and fiat api calls are done + props are set from OrderView
// call merchApi
whenever(
  () =>
    fiatInputs.value.length > 0 &&
    coinInputs.value.length > 0 &&
    typeof props.initialData.areDefaultsFromUrl === "boolean",
  () => {
    if (!props.initialData.areDefaultsFromUrl) {
      setDefaultsByLocalStorage();
      return;
    }

    setFormRefsAndUpdateStore(
      props.initialData.urlData,
      sellForm,
      sellOrderFormStore,
      fiatInputs.value,
      coinInputs.value
    );

    if (
      props.initialData.disableUIKeys &&
      props.initialData.orderMode === "SELL"
    ) {
      provideUiDisable.value = props.initialData.disableUIKeys;
    }
  },
  { deep: true }
);

const handleFormSuccess = async (res) => {
  if (res.data.rel.route === "account") {
    router.push("/verification");
  }
  if (res.data.rel.route === "authentication") {
    router.push("/verification/code");
  }
  if (res.data.rel.route === "prepaid" || res.data.rel.route === "postpaid") {
    await identityQueueStore.fetchIdentityDocuments();
    router.push(identityQueueStore.getNextIdentityRoute);
  }
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);
  if (!orderStore.isOrderFromMerchantApi) {
    await initOrder(); // clears the session
  }
  postRequest(
    "/orders/sell",
    sellOrderFormStore.getFormPayload,
    handleFormSuccess
  );
};
watchFormAndUpdateStore(sellForm, sellOrderFormStore, () => {});
watchAndHandleFiatValidation(sellForm, sellOrderFormStore);
watchAndHandleCoinValidation(sellForm, sellOrderFormStore);
</script>
<style scoped>
.bodycopy {
  margin-bottom: 3rem;
}
</style>
